import { preventDefault } from "@frui.ts/views";
import FilterIcon from "@emanprague/ppaskit/dist/assets/icons/Filter.svg?react";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { Button } from "react-bootstrap";
import Overlay from "react-bootstrap/Overlay";
import type { OverlayInjectedProps } from "react-bootstrap/Overlay";
import "./filterButton.scss";
import { Form } from "@emanprague/ppaskit";

// WARNING: This component was "vibe-fixed" to at least somehow allow some accessibility.
// Those changes hack and work around older older libraries. There might be still few problems with it.

const stopPropagation: React.MouseEventHandler<any> = e => e.stopPropagation();

function assignOverlayCallbackRef(ref: ((instance: HTMLElement) => void) | undefined, value: HTMLElement | null) {
  ref?.(value!);
}

export interface FilterButtonProps {
  id: string;
  confirmLabel?: string;
  clearLabel?: string;

  isActive?: boolean;
  canClear?: boolean;
  onClear?: () => any;
  onConfirm?: () => any;

  customButton?: React.ReactNode;
}

const FilterButton: React.FC<FilterButtonProps> = observer(props => {
  const [isOpened, setOpened] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const toggleRef = React.useRef<HTMLButtonElement | null>(null);
  const popupId = `${props.id}Popup`;
  const titleId = `${props.id}Title`;

  React.useEffect(() => {
    if (!isOpened) {
      return;
    }

    menuRef.current?.focus();
  }, [isOpened]);

  const close = React.useCallback((restoreFocus = false) => {
    setOpened(false);
    if (restoreFocus) {
      toggleRef.current?.focus();
    }
  }, []);

  const onConfirm = () => {
    props.onConfirm?.();
    close(true);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.key !== "Escape") {
      return;
    }

    event.preventDefault();
    close(true);
  };

  const canClear = props.canClear ?? props.isActive;

  const portalContainer = typeof document === "undefined" ? undefined : document.getElementById("root");

  const popperElement = (
    <Overlay
      show={isOpened}
      target={toggleRef.current}
      container={portalContainer}
      placement="bottom-start"
      transition={false}
      rootClose
      rootCloseEvent="mousedown"
      onHide={() => close(false)}
      popperConfig={{
        modifiers: [
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              escapeWithReference: false,
              boundariesElement: "scrollParent",
            },
          },
        ],
      }}>
      {(overlayProps: OverlayInjectedProps) => {
        return (
          <div
            id={popupId}
            ref={element => {
              menuRef.current = element;
              assignOverlayCallbackRef(overlayProps.ref, element);
            }}
            style={overlayProps.style}
            className="filter-button__popup dropdown-menu show"
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={onKeyDown}>
            <Form onSubmit={preventDefault(onConfirm)} className="m-3">
              {props.children}
              <div className="filter-button__popup__footer">
                {canClear && (
                  <Button variant="light" type="button" onClick={props.onClear}>
                    {props.clearLabel}
                  </Button>
                )}
                <Button variant="secondary" type="submit">
                  {props.confirmLabel}
                </Button>
              </div>
            </Form>
          </div>
        );
      }}
    </Overlay>
  );

  return (
    <div className="filter-button" onClick={stopPropagation}>
      <button
        ref={toggleRef}
        type="button"
        id={props.id}
        className={`btn btn-outline-secondary btn-sm dropdown-toggle${props.isActive ? " active" : ""}`}
        aria-controls={popupId}
        aria-expanded={isOpened}
        aria-haspopup="dialog"
        aria-labelledby={titleId}
        onClick={() => setOpened(opened => !opened)}>
        {props.customButton ?? <FilterIcon role="presentation" className="filter-icon" />}
      </button>

      {popperElement}
    </div>
  );
});

export default FilterButton;
